// docs/56 EP4 — the plain blows drawn on the board: a shot (arrow or bolt) flying from the shooter
// to its target along a shallow arc, and blood when a blow lands — a spray away from the attacker
// that falls and leaves a few drops on the ground for a moment. Drawn in the board's effects layer
// with the spell textures; the effect-intensity setting thins it, reduce motion keeps it short.
import Phaser from 'phaser';
import { TEX, ensureSpellTextures } from '../spells/textures';

type P = { x: number; y: number };

export interface CombatFxHost {
  scene: Phaser.Scene;
  /** The board's effects layer (board camera, board pixels). */
  add(o: Phaser.GameObjects.GameObject): void;
  /** Under the figures (the drops on the ground); else `add`. */
  addGround?(o: Phaser.GameObjects.GameObject): void;
  cell: number;
}

export interface CombatQuality { density: number; still: boolean }

/** The longest any shot or drop stays on the board. */
const MAX_LIFE_MS = 3200;
const BLOOD = [0xd8231f, 0xa8140f, 0x7c0b08, 0x5a0605];

export class CombatFx {
  private live = new Set<Phaser.GameObjects.GameObject>();
  private timers: Phaser.Time.TimerEvent[] = [];

  constructor(private readonly host: CombatFxHost, private quality: CombatQuality) { ensureSpellTextures(host.scene); }

  setQuality(q: CombatQuality): void { this.quality = q; }

  clear(): void {
    for (const t of this.timers) t.remove(false);
    this.timers = [];
    for (const o of this.live) o.destroy();
    this.live.clear();
  }

  /**
   * A shot from `from` to `to` over `ms`: it leaves the bow and lands on the target, nose along
   * its arc. `onHit` runs as it lands.
   */
  shot(from: P, to: P, ms: number, kind: 'arrow' | 'bolt' = 'arrow', onHit?: () => void): void {
    const cell = this.host.cell;
    const dist = Math.hypot(to.x - from.x, to.y - from.y);
    const lift = this.quality.still ? 0 : Math.min(cell * 1.2, dist * 0.18);
    const scale = kind === 'bolt' ? 1.2 : 1.4;
    // a dark copy a pixel below the shaft, so it reads on grass and snow as well as on stone
    const shade = this.image(from.x, from.y + 1, TEX.arrow, 0x1a120c).setScale(scale * 1.25).setAlpha(0.8);
    const a = this.image(from.x, from.y, TEX.arrow, kind === 'bolt' ? 0xdfe6f0 : 0xfff1d0).setScale(scale);
    const at = (t: number): P => ({ x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t - lift * 4 * t * (1 - t) });
    // a faint trail of motes behind it so the eye can follow a fast shot
    const trail = this.quality.density > 0.2 ? this.emitter(from.x, from.y, TEX.px, { lifespan: 220, speed: 0, scale: { start: 0.8, end: 0 }, alpha: { start: 0.8, end: 0 }, tint: 0xfff4d6, frequency: 14 }) : undefined;
    const state = { t: 0 };
    const place = () => {
      const p = at(state.t), q = at(Math.min(1, state.t + 0.02)), r = Math.atan2(q.y - p.y, q.x - p.x) + Math.PI / 2;
      a.setPosition(p.x, p.y).setRotation(r);
      shade.setPosition(p.x, p.y + 1).setRotation(r);
      trail?.setPosition(p.x, p.y);
    };
    place();
    this.host.scene.tweens.add({
      targets: state, t: 1, duration: Math.max(1, ms), ease: 'Linear',
      onUpdate: place,
      onComplete: () => {
        this.kill(a); this.kill(shade);
        if (trail) { trail.stop(); this.reap(trail, 240); }
        onHit?.();
      },
    });
  }

  /**
   * Blood off a struck figure at `at` (its chest), thrown away from `from` (the attacker; none =
   * straight up), more for a heavier blow; a few drops land and fade on the ground below.
   */
  blood(at: P, from: P | undefined, dealt: number, feetY: number): void {
    const cell = this.host.cell;
    const dir = from ? Phaser.Math.RadToDeg(Math.atan2(at.y - from.y, at.x - from.x)) : -90;
    const n = this.n(Math.min(30, 8 + dealt * 3));
    const life = this.quality.still ? 380 : 700;
    // (Phaser 4 explodes at x/y relative to the emitter: one-shot emitters sit at the origin)
    const spray = this.emitter(0, 0, TEX.px, {
      emitting: false, lifespan: { min: life * 0.6, max: life }, speed: { min: cell * 1.2, max: cell * 4.2 },
      angle: { min: dir - 55, max: dir + 55 }, gravityY: cell * 14, scale: { start: 1.1, end: 0.5 }, alpha: { start: 1, end: 0.2 }, tint: BLOOD,
    });
    spray.explode(n, at.x, at.y);
    this.reap(spray, life);
    // a puff of finer mist at the wound
    const mist = this.emitter(0, 0, TEX.dot, { emitting: false, lifespan: 320, speed: { min: 4, max: cell * 1.2 }, angle: { min: dir - 80, max: dir + 80 }, scale: { start: 1.2, end: 0 }, alpha: { start: 0.8, end: 0 }, tint: [0xe0301f, 0x9a120c] });
    mist.explode(this.n(8), at.x, at.y);
    this.reap(mist, 320);
    // drops on the ground under it, which soak away
    if (this.quality.still) return;
    const lean = from ? Math.sign(at.x - from.x) : 0;
    const drops = Math.max(2, Math.min(6, Math.round(this.n(2 + dealt))));
    for (let i = 0; i < drops; i++) {
      const x = at.x + lean * cell * (0.15 + Math.random() * 0.45) + (Math.random() - 0.5) * cell * 0.5;
      const y = feetY - cell * (0.05 + Math.random() * 0.2);
      this.after(life * (0.35 + Math.random() * 0.4), () => {
        const d = this.image(x, y, TEX.px, BLOOD[1 + (i % 3)]!, true).setScale(0.5 + Math.random() * 0.8, 0.4 + Math.random() * 0.4).setAlpha(0.9);
        this.host.scene.tweens.add({ targets: d, alpha: 0, duration: 900, delay: 900, onComplete: () => this.kill(d) });
      });
    }
  }

  /**
   * docs/103 RB6: a flank's flourish over `ms`. Two daggers close on the foe at `at` from the
   * striker's side (`from`) and the friend's across (`across`) on gold trails; where they meet
   * (45 % in) a flash, a ring and a burst of sparks. A backstab is gold with a crimson ring and a
   * crossed slash; a misdirection's +2 is the same pincer, smaller and paler.
   */
  flank(at: P, from: P, across: P | undefined, ms: number, backstab: boolean): void {
    const cell = this.host.cell, meet = ms * 0.45;
    const gold = backstab ? 0xffd34d : 0xf4e7b8;
    const sides = [from, across ?? { x: at.x * 2 - from.x, y: at.y * 2 - from.y }];
    for (const side of sides) {
      const ang = Math.atan2(at.y - side.y, at.x - side.x);
      const start = { x: at.x - Math.cos(ang) * cell * 0.9, y: at.y - Math.sin(ang) * cell * 0.9 };
      const d = this.image(start.x, start.y, TEX.dagger, 0xf3f6ff).setScale(backstab ? 2.4 : 1.7).setRotation(ang + Math.PI / 2).setAlpha(0.35);
      const trail = this.quality.density > 0.2 ? this.emitter(start.x, start.y, TEX.px, { lifespan: 260, speed: 0, scale: { start: 1, end: 0 }, alpha: { start: 0.9, end: 0 }, tint: gold, frequency: 12, blendMode: 'ADD' }) : undefined;
      this.host.scene.tweens.add({
        targets: d, alpha: 1, x: at.x - Math.cos(ang) * cell * 0.18, y: at.y - Math.sin(ang) * cell * 0.18,
        duration: Math.max(1, meet), ease: 'Cubic.easeIn',
        onUpdate: () => trail?.setPosition(d.x, d.y),
        onComplete: () => {
          trail?.stop(); if (trail) this.reap(trail, 280);
          this.host.scene.tweens.add({ targets: d, alpha: 0, duration: Math.max(1, ms * 0.25), onComplete: () => this.kill(d) });
        },
      });
    }
    this.after(meet, () => {
      const flash = this.image(at.x, at.y, TEX.glow, gold).setBlendMode('ADD').setScale(0.2).setAlpha(0.95);
      this.host.scene.tweens.add({ targets: flash, scale: backstab ? 1.6 : 1, alpha: 0, duration: Math.max(1, ms * 0.5), ease: 'Quad.easeOut', onComplete: () => this.kill(flash) });
      const sparks = this.emitter(0, 0, TEX.star, {
        emitting: false, lifespan: { min: 260, max: this.quality.still ? 300 : 620 }, speed: { min: cell * 1.5, max: cell * (backstab ? 5 : 3) },
        angle: { min: 0, max: 360 }, scale: { start: backstab ? 1.3 : 0.9, end: 0 }, alpha: { start: 1, end: 0 }, tint: [gold, 0xffffff, 0xffb02e], blendMode: 'ADD', gravityY: cell * 4,
      });
      sparks.explode(this.n(backstab ? 26 : 12), at.x, at.y);
      this.reap(sparks, 640);
      if (!backstab) return;
      const ring = this.image(at.x, at.y, TEX.ring, 0xe0301f).setScale(0.3).setAlpha(0.95);
      this.host.scene.tweens.add({ targets: ring, scale: 2.2, alpha: 0, duration: Math.max(1, ms * 0.55), ease: 'Cubic.easeOut', onComplete: () => this.kill(ring) });
      // the crossed slash: two quick strokes through the foe
      const g = this.host.scene.add.graphics();
      this.own(g);
      const r = cell * 0.55;
      g.lineStyle(Math.max(2, cell * 0.09), 0xfff4d6, 1);
      g.lineBetween(at.x - r, at.y - r, at.x + r, at.y + r);
      g.lineBetween(at.x + r, at.y - r, at.x - r, at.y + r);
      g.lineStyle(Math.max(1, cell * 0.04), 0xe0301f, 1);
      g.lineBetween(at.x - r * 0.8, at.y - r * 0.8 + 2, at.x + r * 0.8, at.y + r * 0.8 + 2);
      g.lineBetween(at.x + r * 0.8, at.y - r * 0.8 + 2, at.x - r * 0.8, at.y + r * 0.8 + 2);
      this.host.scene.tweens.add({ targets: g, alpha: 0, duration: Math.max(1, ms * 0.5), delay: ms * 0.12, onComplete: () => this.kill(g) });
    });
  }

  /**
   * docs/104 PD2: a paladin takes the blow meant for a friend, over `ms` (< 500). A streak of gold
   * light leaps from the paladin's chest to the friend's, a shield of light flares on the friend
   * and sparks fly where it lands; the paladin's own leap is the player's (EffectPlayer).
   */
  interpose(paladin: P, ward: P, ms: number): void {
    const cell = this.host.cell;
    const gold = 0xffd34d;
    const g = this.host.scene.add.graphics().setBlendMode('ADD');
    this.own(g);
    const st = { t: 0 };
    this.host.scene.tweens.add({
      targets: st, t: 1, duration: Math.max(1, ms * 0.35), ease: 'Cubic.easeOut',
      onUpdate: () => {
        // the light runs out from the paladin and reaches the friend a third of the way in
        const x = paladin.x + (ward.x - paladin.x) * st.t, y = paladin.y + (ward.y - paladin.y) * st.t - Math.sin(Math.PI * st.t) * cell * 0.3;
        g.clear();
        g.lineStyle(Math.max(3, cell * 0.16), gold, 0.35).lineBetween(paladin.x, paladin.y, x, y);
        g.lineStyle(Math.max(1, cell * 0.06), 0xfffbe0, 1).lineBetween(paladin.x, paladin.y, x, y);
      },
      onComplete: () => this.host.scene.tweens.add({ targets: g, alpha: 0, duration: Math.max(1, ms * 0.5), onComplete: () => this.kill(g) }),
    });
    this.after(ms * 0.3, () => {
      const glow = this.image(ward.x, ward.y, TEX.glow, gold).setBlendMode('ADD').setScale(0.2).setAlpha(1);
      this.host.scene.tweens.add({ targets: glow, scale: (cell * 1.8) / 64, alpha: 0, duration: Math.max(1, ms * 0.65), ease: 'Quad.easeOut', onComplete: () => this.kill(glow) });
      const sh = this.image(ward.x, ward.y - cell * 0.05, TEX.shield, gold).setScale((cell * 0.3) / 24).setAlpha(1);
      this.host.scene.tweens.add({ targets: sh, scale: (cell * 0.95) / 24, duration: Math.max(1, ms * 0.25), ease: 'Back.easeOut' });
      this.host.scene.tweens.add({ targets: sh, alpha: 0, duration: Math.max(1, ms * 0.4), delay: ms * 0.3, onComplete: () => this.kill(sh) });
      const sparks = this.emitter(0, 0, TEX.star, { emitting: false, lifespan: { min: 200, max: 420 }, speed: { min: cell * 1.2, max: cell * 3 }, angle: { min: 0, max: 360 }, scale: { start: 1, end: 0 }, alpha: { start: 1, end: 0 }, tint: [0xffffff, gold, 0xffb02e], blendMode: 'ADD' });
      sparks.explode(this.n(14), ward.x, ward.y);
      this.reap(sparks, 440);
    });
  }

  /**
   * docs/104 BD2: an Encore gives a friend their abilities back sooner — a little gold clock face
   * pops over its head, its hands spinning back, with sparkles wheeling round it.
   */
  hasten(head: P, ms: number): void {
    const cell = this.host.cell;
    const gold = 0xffe28a;
    const at = { x: head.x, y: head.y - cell * 0.15 };
    const face = this.image(at.x, at.y, TEX.clock, gold).setScale(0.3).setAlpha(0);
    const hands = this.host.scene.add.graphics();
    this.own(hands);
    const glow = this.image(at.x, at.y, TEX.glow, gold).setBlendMode('ADD').setScale((cell * 0.9) / 64).setAlpha(0);
    const stars = [0, 1, 2, 3].map(() => this.image(at.x, at.y, TEX.star, 0xffffff).setBlendMode('ADD').setScale(0.9));
    const st = { t: 0 };
    const s = (cell * 0.75) / 11;
    this.host.scene.tweens.add({
      targets: st, t: 1, duration: Math.max(1, ms), ease: 'Linear',
      onUpdate: () => {
        const t = st.t;
        // pops in, holds, fades out
        const a = t < 0.2 ? t / 0.2 : t > 0.75 ? (1 - t) / 0.25 : 1;
        face.setScale(s * (t < 0.2 ? 0.5 + 0.5 * Phaser.Math.Easing.Back.Out(t / 0.2) : 1)).setAlpha(a);
        glow.setAlpha(0.55 * a);
        // the hands turn backwards, fast: time given back
        const spin = this.quality.still ? 0 : -t * Math.PI * 6;
        const r = cell * 0.26;
        hands.clear().setAlpha(a);
        hands.lineStyle(Math.max(1, cell * 0.06), 0x3a2410, 1).lineBetween(at.x, at.y, at.x + Math.cos(spin - Math.PI / 2) * r, at.y + Math.sin(spin - Math.PI / 2) * r);
        hands.lineStyle(Math.max(1, cell * 0.05), 0x3a2410, 1).lineBetween(at.x, at.y, at.x + Math.cos(spin * 0.25) * r * 0.6, at.y + Math.sin(spin * 0.25) * r * 0.6);
        stars.forEach((m, i) => {
          const q = (this.quality.still ? 0 : t * Math.PI * 3) + (i * Math.PI) / 2;
          m.setPosition(at.x + Math.cos(q) * cell * 0.55, at.y + Math.sin(q) * cell * 0.3).setAlpha(a * (Math.sin(q) > 0 ? 1 : 0.4));
        });
      },
      onComplete: () => { this.kill(face); this.kill(hands); this.kill(glow); for (const m of stars) this.kill(m); },
    });
  }

  /**
   * docs/104 DR2: the ground changes under a druid's hand — the moment on every square, before the
   * tiles redraw from the board: a thicket sprouts (tufts and leaves burst up), rough ground heaves
   * (dust and stones thrown up, a dark blot), cleared ground loses its growth (dry leaves blown off).
   */
  grow(points: readonly P[], into: string, ms: number): void {
    const cell = this.host.cell;
    const pts = points.slice(0, 16);
    const step = Math.min(45, ms / Math.max(1, pts.length + 2));
    pts.forEach((p, i) => this.after(i * step, () => {
      const foot = { x: p.x, y: p.y + cell * 0.25 };
      if (into === 'Thicket') {
        // bright leaves on a dark rim, so a tuft reads on grass as well as on stone
        const greens = [0x8ad84e, 0xa8e868, 0x6cc23e];
        for (let k = 0; k < 3; k++) {
          const x = foot.x + (k - 1) * cell * 0.28 + (Math.random() - 0.5) * cell * 0.1, y = foot.y - (k % 2) * cell * 0.12;
          const full = (cell * (0.42 + Math.random() * 0.16)) / 9;
          const rim = this.image(x, y + 1, TEX.tuft, 0x16300c).setOrigin(0.5, 1).setScale(0.1);
          const t = this.image(x, y, TEX.tuft, greens[k]!).setOrigin(0.5, 1).setScale(0.1);
          this.host.scene.tweens.add({ targets: rim, scaleX: full * 1.25, scaleY: full * 1.18, duration: Math.max(1, Math.min(260, ms * 0.5)), delay: k * 40, ease: 'Back.easeOut' });
          this.host.scene.tweens.add({ targets: t, scale: full, duration: Math.max(1, Math.min(260, ms * 0.5)), delay: k * 40, ease: 'Back.easeOut' });
          // the tufts hold until the board's own thicket is drawn under them, then fade
          this.host.scene.tweens.add({ targets: [t, rim], alpha: 0, duration: 500, delay: ms + 900, onComplete: () => { this.kill(t); this.kill(rim); } });
        }
        const leaves = this.emitter(0, 0, TEX.leaf, { emitting: false, lifespan: { min: 500, max: 900 }, speed: { min: cell * 0.8, max: cell * 2.2 }, angle: { min: 200, max: 340 }, gravityY: cell * 4, rotate: { min: 0, max: 360 }, scale: { start: 1.2, end: 0.6 }, alpha: { start: 1, end: 0 }, tint: [0x9be06a, 0x5aa83a, 0x3f8a2a] });
        leaves.explode(this.n(7), foot.x, foot.y);
        this.reap(leaves, 920);
      } else if (into === 'Bad') {
        const blot = this.image(foot.x, foot.y, TEX.scorch, 0x4a3420, true).setScale((cell * 1) / 32, (cell * 0.5) / 32).setAlpha(0);
        this.host.scene.tweens.add({ targets: blot, alpha: 0.5, duration: 160 });
        this.host.scene.tweens.add({ targets: blot, alpha: 0, duration: 600, delay: ms + 700, onComplete: () => this.kill(blot) });
        const dust = this.emitter(0, 0, TEX.smoke, { emitting: false, lifespan: { min: 600, max: 1000 }, speed: { min: cell * 0.3, max: cell * 1 }, angle: { min: 200, max: 340 }, scale: { start: 0.5, end: 1.3 }, alpha: { start: 0.55, end: 0 }, tint: [0xb89a6a, 0x8a6a3a] });
        dust.explode(this.n(5), foot.x, foot.y);
        this.reap(dust, 1020);
        const stones = this.emitter(0, 0, TEX.pebble, { emitting: false, lifespan: { min: 380, max: 620 }, speed: { min: cell * 1.2, max: cell * 2.6 }, angle: { min: 235, max: 305 }, gravityY: cell * 14, rotate: { min: 0, max: 360 }, scale: { start: 1.2, end: 0.9 }, tint: [0x7a5a34, 0x5a4024, 0x9a7a4a] });
        stones.explode(this.n(6), foot.x, foot.y);
        this.reap(stones, 640);
      } else {
        // cleared: dry leaves and stalks blown away to one side, a wisp of dust
        const dry = this.emitter(0, 0, TEX.leaf, { emitting: false, lifespan: { min: 700, max: 1200 }, speedX: { min: cell * 1, max: cell * 2.6 }, speedY: { min: -cell * 1.2, max: -cell * 0.2 }, gravityY: cell * 1.5, rotate: { min: 0, max: 360 }, scale: { start: 1.1, end: 0.7 }, alpha: { start: 1, end: 0 }, tint: [0xc9a24a, 0x9a6a2a, 0x7a4a1a] });
        dry.explode(this.n(8), foot.x, foot.y);
        this.reap(dry, 1220);
        const wisp = this.emitter(0, 0, TEX.smoke, { emitting: false, lifespan: 700, speedX: { min: cell * 0.4, max: cell * 1.2 }, speedY: { min: -cell * 0.3, max: 0 }, scale: { start: 0.4, end: 1 }, alpha: { start: 0.35, end: 0 }, tint: [0xd8c8a0, 0xa89870] });
        wisp.explode(this.n(3), foot.x, foot.y);
        this.reap(wisp, 720);
      }
    }));
  }

  private n(base: number): number { return Math.max(1, Math.round(base * Math.max(0.2, this.quality.density))); }

  private image(x: number, y: number, key: string, tint: number, ground = false): Phaser.GameObjects.Image {
    const img = this.host.scene.add.image(x, y, key).setTint(tint);
    this.own(img, ground);
    return img;
  }

  private emitter(x: number, y: number, tex: string, cfg: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig): Phaser.GameObjects.Particles.ParticleEmitter {
    const e = this.host.scene.add.particles(x, y, tex, cfg);
    this.own(e);
    return e;
  }

  private own(o: Phaser.GameObjects.GameObject, ground = false): void {
    if (ground && this.host.addGround) this.host.addGround(o); else this.host.add(o);
    this.live.add(o);
    // whatever happens to its tweens (a new board kills them all), nothing outlives MAX_LIFE_MS
    this.after(MAX_LIFE_MS, () => { if (this.live.has(o)) this.kill(o); });
  }

  private kill(o: Phaser.GameObjects.GameObject): void { this.live.delete(o); o.destroy(); }

  private reap(e: Phaser.GameObjects.Particles.ParticleEmitter, ms: number): void { this.after(ms + 60, () => { if (this.live.has(e)) this.kill(e); }); }

  private after(ms: number, fn: () => void): void {
    const t = this.host.scene.time.delayedCall(Math.max(0, ms), () => { this.timers = this.timers.filter((x) => x !== t); fn(); });
    this.timers.push(t);
  }
}
